import React, { useRef, Suspense, lazy, useCallback } from 'react';
import _ from 'lodash';
import {
  DOCUMENT_HEADER,
  PREVIEW_ID,
  BREAKPOINT_MAX_VALUE,
} from '@src/utils/constants';
import { useAppContext, ACTIONS } from '@components/Contexts/AppContext';
import {
  calcBreakpointWidth,
  getHeaderPortalRoot,
  updateSiteUrlWithWindowOrigin,
} from '@src/utils/frameHelpers';
import Portal from '@components/Portal';
import feedbackPinStyle from '@components/FeedbackPin/FeedbackPinStyle';
import s from '../SiteRenderer.scss';
import { addParamToUrl } from '@src/utils/urlUtils';

const FeedbackPin = lazy(() => import('@components/FeedbackPin/FeedbackPin'));

interface IResponsiveRendererProps {
  handleFrameLoaded: (
    previewEl: React.MutableRefObject<HTMLIFrameElement>,
  ) => void;
}

const ResponsiveRenderer = ({
  handleFrameLoaded,
}: IResponsiveRendererProps) => {
  const {
    dispatch,
    state: { loading, siteUrl, currentBreakPoint, pageId, comments },
  } = useAppContext();
  const previewEl = useRef() as React.MutableRefObject<HTMLIFrameElement>;

  const getUrlByViewMode = useCallback(() => {
    let newUrl = siteUrl;
    if (currentBreakPoint.canvasSize < 750) {
      newUrl = addParamToUrl(siteUrl, 'showMobileView', 'true');
    }
    return updateSiteUrlWithWindowOrigin(newUrl);
  }, [siteUrl, currentBreakPoint]);

  const renderPortals = useCallback(() => {
    const headerComments = _.filter(comments, comment => {
      const { pageId, breakpoint } = comment.commentLocation;
      const { minWidth, maxWidth } = breakpoint;
      const { max, min } = currentBreakPoint;
      if (comment.isMigrated) {
        return pageId === DOCUMENT_HEADER;
      }
      return pageId === DOCUMENT_HEADER && minWidth === min && maxWidth === max;
    });
    const currentPageComments = _.filter(comments, comment => {
      const { breakpoint } = comment.commentLocation;
      const { minWidth, maxWidth } = breakpoint;
      const { max, min } = currentBreakPoint;
      if (comment.commentLocation.innerPath) {
        return (
          pageId === comment.commentLocation.pageId &&
          minWidth === min &&
          maxWidth === max &&
          comment.commentLocation.innerPath ===
            previewEl.current?.contentWindow?.location?.pathname
        );
      }
      return (
        pageId === comment.commentLocation.pageId &&
        minWidth === min &&
        maxWidth === max
      );
    });

    return (
      <>
        <Portal
          closeAllComments={() =>
            dispatch({
              type: ACTIONS.DE_ACTIVATE_COMMENTS_AND_CLEAN_NEW_COMMENTS,
            })
          }
          portalRoot={getHeaderPortalRoot(previewEl)}
        >
          <style>{feedbackPinStyle()}</style>
          {headerComments.map(comment => (
            <Suspense fallback="">
              <FeedbackPin
                isHeader={true}
                key={comment.id}
                comment={comment}
                pageYOffset={previewEl.current.contentWindow?.pageYOffset!}
                previewEl={previewEl}
              />
            </Suspense>
          ))}
        </Portal>
        <Portal
          closeAllComments={() =>
            dispatch({
              type: ACTIONS.DE_ACTIVATE_COMMENTS_AND_CLEAN_NEW_COMMENTS,
            })
          }
          portalRoot={previewEl.current.contentDocument!.getElementById(pageId)}
        >
          <style>{feedbackPinStyle()}</style>
          {currentPageComments.map(comment => (
            <Suspense fallback="">
              <FeedbackPin
                key={comment.id}
                comment={comment}
                pageYOffset={previewEl.current.contentWindow?.pageYOffset!}
                previewEl={previewEl}
              />
            </Suspense>
          ))}
        </Portal>
      </>
    );
  }, [comments, previewEl, pageId, currentBreakPoint, dispatch]);

  function renderResponsivePreview() {
    const breakpointWidth = calcBreakpointWidth({ ...currentBreakPoint });

    const width =
      currentBreakPoint.max === BREAKPOINT_MAX_VALUE ? '100%' : breakpointWidth;
    return (
      <>
        <div style={{ margin: '0 auto', display: 'grid', width }}>
          <iframe
            id={PREVIEW_ID}
            title="feedback-viewer"
            ref={previewEl}
            style={{ width }}
            className={s.editorIframe}
            src={getUrlByViewMode()}
            data-hook="site-renderer-preview"
            onLoad={() => handleFrameLoaded(previewEl)}
          ></iframe>
          {comments.length > 0 && !loading ? renderPortals() : <></>}
        </div>
      </>
    );
  }

  return (
    <div className={s.siteRenderer} data-hook="site-renderer">
      <div className={s.iframeWrapper}>
        <div className={s.iframeContainer}>{renderResponsivePreview()}</div>
      </div>
    </div>
  );
};

export default ResponsiveRenderer;
